import {getKeyrusAiLocaleKey} from 'helpers/keyrusAi'

// Localized copy for the Keyrus AI landing sections (hero, "Meet Keyrus", HOM,
// the eras timeline, contact). English lives as defaults inside each section
// component; this module only carries the translated overrides. A locale with
// no entry here (en/fr/pt) falls back to those English defaults. Spanish copy
// comes from "LANDING PAGE TEXT SP - KEYRUS AI".
const ES = {
  hero: {
    line1: 'El futuro de los negocios',
    line2: 'funcionará con inteligencia,',
    tail: 'no con software.',
  },
  about: {
    titleLine1: 'Conoce Keyrus:',
    titleLine2: 'Arquitectos de Inteligencia',
    subtitle:
      'Transformamos la inteligencia en resultados, no solo en herramientas de IA.',
    cards: {
      shift: {
        title: 'El cambio',
        bodyLead:
          'La inteligencia distribuida se convierte en la infraestructura de las organizaciones. Surge una nueva',
        bodyEmphasis:
          'categoría: empresas que operan impulsadas por inteligencia, no por software.',
      },
      promise: {
        title: 'La promesa',
        bodyLead:
          'Ayudamos a las organizaciones a evolucionar de la IA experimental a la IA empresarial escalable.',
        bodyEmphasis:
          'De agentes aislados a sistemas orquestados, de los datos a la ejecución.',
      },
      framework: {
        title: 'El modelo',
        bodyLead:
          'El Human Orchestrated Model™ (HOM™) integra tres capas que convierten los datos, los agentes inteligentes',
        bodyEmphasis:
          'y la gobernanza humana en resultados medibles para el negocio.',
      },
    },
  },
  hom: {
    title: 'Cómo funciona el Human Orchestrated Model™ (HOM™)',
    subtitle:
      'Los datos habilitan la inteligencia. La gobernanza la dirige. La medición la optimiza continuamente.',
    callouts: {
      performance: {
        title: 'Performance Steering',
        body: [
          'Integramos métricas y mecanismos de medición desde el diseño de los sistemas inteligentes.',
          'Así garantizamos que cada capacidad inteligente esté alineada con objetivos de negocio y evolucione continuamente para mejorar su desempeño.',
        ],
      },
      human: {
        title: 'Human in Command',
        body: [
          'Alineamos la inteligencia artificial con los objetivos del negocio mediante modelos de supervisión humana.',
          'Garantizamos que la inteligencia opere bajo principios de control, transparencia y responsabilidad, evitando el caos agéntico.',
        ],
      },
      foundation: {
        title: 'The Intelligence Foundations',
        body: [
          'Construimos bases sólidas de datos y operaciones que permiten escalar la inteligencia de forma confiable.',
          'Transformamos iniciativas experimentales en capacidades empresariales sostenibles.',
        ],
      },
    },
  },
  eras: {
    headerLine1: 'La IA por sí sola no transforma los negocios.',
    headerLine2: 'La inteligencia con arquitectura sí.',
    headerLine3: 'Y en Keyrus llevamos más de 30 años haciéndolo realidad.',
    bodies: {
      business:
        'Trabajamos con organizaciones para construir sus primeras bases sólidas de datos. A través de iniciativas de Data Warehousing y procesos ETL, conectamos información dispersa y ayudamos a las empresas a obtener algo que antes no tenían: una única fuente confiable y estructurada de información para la toma de decisiones.',
      digital:
        'Ayudamos a las organizaciones a conectar sus sistemas internos con un ecosistema digital en constante expansión. Desarrollamos las soluciones que hicieron posible la visualización de información en tiempo real mediante dashboards y analítica avanzada, poniendo inteligencia accionable en manos de los líderes para tomar decisiones a la velocidad que exigía el negocio.',
      data: 'Ayudamos a las organizaciones a extraer valor de grandes volúmenes de datos. Identificamos patrones donde otros solo veían complejidad. La inteligencia dejó de enfocarse únicamente en comprender lo que había ocurrido o lo que estaba sucediendo; comenzó a revelar oportunidades, riesgos y conocimientos que permanecían ocultos.',
      predictive:
        'Ayudamos a las organizaciones a convertir grandes volúmenes de datos en conocimiento accionable. Identificamos patrones donde otros solo veían complejidad. La inteligencia dejó de centrarse únicamente en comprender lo que había ocurrido o lo que estaba sucediendo; comenzó a revelar oportunidades, riesgos y conocimientos que permanecían ocultos.',
      augmented:
        'Ayudamos a las organizaciones a incorporar la IA Generativa y los sistemas agénticos en el núcleo de sus operaciones, potenciando la capacidad de las personas para tomar mejores decisiones. Desde el inicio entendimos que la inteligencia artificial y la inteligencia humana no compiten entre sí; trabajan juntas para amplificar el desempeño, acelerar la ejecución y generar mejores resultados de negocio.',
    },
  },
  contact: {
    title: 'Keyrus, Arquitectos de la inteligencia',
    subtitle: 'Operacionalizamos la inteligencia.',
    submitLabel: 'Contacta con nuestros arquitectos',
    hubspotLocale: 'es',
    // Client-side relabels for the embedded HubSpot form, keyed by the field's
    // `name` attribute. HubSpot serves the form in English; KeyrusAiContact
    // rewrites these labels once the form is ready. From "LANDING PAGE TEXT SP".
    fields: {
      firstname: 'Nombre',
      lastname: 'Apellido',
      email: 'Correo corporativo',
      phone: 'Teléfono',
      company: 'Empresa',
      message: 'Mensaje',
    },
    policy: {
      consentLead:
        'Al enviar este formulario, aceptas que Keyrus pueda tratar la información proporcionada para responder a tu solicitud. Para saber cómo tratamos tus datos personales y tus derechos, lee nuestra',
      privacyLabel: 'Política de Privacidad',
      moreLead: 'Para más información, consulta nuestro',
      termsLabel: 'Aviso Legal y Condiciones de Uso',
    },
  },
  nav: [
    {label: 'La nueva categoría', href: '#hero'},
    {label: 'Conoce Keyrus', href: '#meet-keyrus'},
    {label: 'HOM™', href: '#hom'},
    {label: 'La era de la inteligencia', href: '#intelligence-eras'},
    {label: 'Contacto', href: '#contact'},
  ],
}

// European Portuguese (pt-PT, Portugal) copy from
// "Keyrus_Architect_of_Intelligence_PTPT_Final". Like the Brazilian source it
// translates the era titles and the third HOM callout title, so `eras.titles`
// is provided here. The "Architect of intelligence" slogan is kept in English
// on purpose (brand tagline), like the banner.
const PT_PT = {
  // Page <title> and meta description for the standalone keyrus-ai page,
  // consumed by the keyrus-ai template via getKeyrusAiContent. Derived from the
  // pt-PT manifesto; confirm with the Portugal team.
  meta: {
    title: 'Keyrus Ai — Architect of intelligence',
    description:
      'O futuro dos negócios será impulsionado pela inteligência, não pelo software. Descubra a Keyrus Ai.',
  },
  hero: {
    line1: 'O futuro dos negócios',
    line2: 'será impulsionado pela inteligência,',
    tail: 'não pelo software.',
  },
  about: {
    titleLine1: 'Conheça a Keyrus:',
    // Slogan and subtitle kept in English on purpose (brand tagline), like the
    // banner. Javier asked for "We operationalise intelligence" in English
    // everywhere — do not translate it back to Portuguese.
    titleLine2: 'Architect of intelligence',
    subtitle: 'We operationalize intelligence, not AI tools',
    cards: {
      shift: {
        title: 'A Mudança',
        bodyLead:
          'A inteligência distribuída torna-se a infraestrutura das organizações. Surge uma nova',
        bodyEmphasis:
          'categoria: empresas que funcionam com base na inteligência, e não no software.',
      },
      promise: {
        title: 'A Promessa',
        bodyLead:
          'Ajudamos as organizações a evoluir da IA experimental para a IA industrial.',
        bodyEmphasis:
          'De agentes isolados para sistemas orquestrados. Do insight à execução.',
      },
      framework: {
        title: 'O Modelo',
        bodyLead:
          'O Human Orchestrated Model™ — três camadas que transformam dados, agentes',
        bodyEmphasis: 'e governação humana em desempenho mensurável.',
      },
    },
  },
  hom: {
    title: 'Como funciona o Human Orchestrated Model™:',
    subtitle:
      'Os dados permitem a inteligência. A governação orienta-a. A medição melhora-a.',
    callouts: {
      performance: {
        title: 'Orientação de Performance',
        body: [
          'Abordamos os desafios de geração de valor e seleção de casos de uso incorporando métricas diretamente na conceção dos sistemas inteligentes.',
          'Garantimos que cada capacidade inteligente está ligada a impacto real no negócio e aprende continuamente para melhorar o seu desempenho.',
        ],
      },
      human: {
        title: 'Humano no Comando',
        body: [
          'Respondemos aos desafios de alinhamento organizacional transformando a IA de uma capacidade técnica num sistema organizacional governado.',
          'Garantimos que a inteligência opera num enquadramento controlado, transparente e responsável, evitando o caos associado a agentes autónomos.',
        ],
      },
      foundation: {
        title: 'A Base da Inteligência',
        body: [
          'Respondemos aos desafios de rigor dos dados e financeiro através da construção de fundações de dados sólidas, governadas e operacionais.',
          'Permitimos que os sistemas inteligentes compreendam o contexto e atuem de forma eficaz. Fazemos a transição da IA experimental para a IA industrializada.',
        ],
      },
    },
  },
  eras: {
    headerLine1: 'A IA sozinha não transforma os negócios.',
    headerLine2: 'A inteligência arquitetada, sim.',
    headerLine3: 'E a Keyrus tem feito isto há mais de 30 anos:',
    titles: {
      business: 'Business Intelligence',
      digital: 'Inteligência Digital',
      data: 'Inteligência de Dados',
      predictive: 'Inteligência Preditiva',
      augmented: 'Inteligência Aumentada',
      architected: 'Inteligência Arquitetada',
    },
    bodies: {
      business:
        'Trabalhámos com organizações para construir as suas primeiras fundações de dados. Através de data warehousing e ETL, ligámos aquilo que estava fragmentado e demos às empresas algo que muitas nunca tinham tido antes: uma versão única e estruturada da verdade.',
      digital:
        'Ajudámos as organizações a ligar os pontos entre os sistemas internos e o ecossistema digital em expansão. Construímos as soluções que tornaram possíveis os dashboards em tempo real. Colocámos inteligência em tempo real nas mãos dos líderes que precisavam de tomar decisões à velocidade exigida pelos seus negócios.',
      data: 'A Keyrus ajudou as organizações a dar sentido aos grandes volumes de dados. Ajudámos a encontrar padrões no ruído. A inteligência deixou de ser apenas compreender o que aconteceu ou o que estava a acontecer naquele momento. Passou a ser descobrir aquilo que estava oculto.',
      predictive:
        'Ajudámos os nossos clientes a passar de “eis o que aconteceu” para “eis o que vem a seguir”, desenvolvendo modelos e capacidades que tornaram a previsão operacional, e não apenas experimental.',
      augmented:
        'A Keyrus ajudou a introduzir a IA generativa e agêntica nas organizações de formas que reforçaram a capacidade humana de tomada de decisão, em vez de a substituir. Sempre vimos a IA e a inteligência humana como parceiras, e não como concorrentes.',
    },
  },
  contact: {
    title: 'Keyrus, Architect of intelligence',
    // Tagline kept in English on purpose (Javier's request).
    subtitle: 'We operationalize intelligence.',
    submitLabel: 'Contacte os nossos Arquitetos',
    hubspotLocale: 'pt-br',
    // Client-side relabels for the embedded HubSpot form (served in English),
    // keyed by the field's `name` attribute. Rewritten by KeyrusAiContact.
    fields: {
      firstname: 'Nome',
      lastname: 'Apelido',
      email: 'E-mail profissional',
      phone: 'Telefone',
      company: 'Empresa',
      message: 'Mensagem',
    },
    policy: {
      consentLead:
        'Ao enviar este formulário, concorda que a Keyrus poderá tratar as informações fornecidas para responder ao seu pedido. Para saber como tratamos os seus dados pessoais e os seus direitos, leia a nossa',
      privacyLabel: 'Política de Privacidade',
      moreLead: 'Para mais informações, consulte o nosso',
      termsLabel: 'Aviso Legal e Termos de Utilização',
    },
  },
  nav: [
    {label: 'A nova categoria', href: '#hero'},
    {label: 'Conheça a Keyrus', href: '#meet-keyrus'},
    {label: 'HOM™', href: '#hom'},
    {label: 'A era da inteligência', href: '#intelligence-eras'},
    {label: 'Contacto', href: '#contact'},
  ],
}

// Brazilian Portuguese (pt-BR, Brazil) copy from "Keyrus Ai _ LP pt-br". Unlike
// the Spanish source, this one also translates the era titles and the third HOM
// callout title, so `eras.titles` is provided here.
const PT_BR = {
  // Page <title> and meta description for the standalone keyrus-ai page,
  // consumed by the keyrus-ai template via getKeyrusAiContent.
  meta: {
    title: 'Keyrus Ai — Architect of intelligence',
    description:
      'O futuro dos negócios funcionará com inteligência, não com softwares. Descubra a Keyrus Ai.',
  },
  hero: {
    line1: 'No futuro, os negócios',
    line2: 'funcionarão com inteligência,',
    tail: 'não com software.',
  },
  about: {
    titleLine1: 'Conheça a Keyrus:',
    // Slogan kept in English on purpose (brand tagline). The subtitle is
    // translated for Brazil per client feedback (July 2026), overriding
    // Javier's earlier English-everywhere request.
    titleLine2: 'Architect of intelligence',
    subtitle:
      'Nós operacionalizamos a inteligência, não apenas ferramentas de IA',
    cards: {
      shift: {
        title: 'A mudança',
        bodyLead:
          'A inteligência distribuída passa a ser a infraestrutura da empresa. Assim, surge uma nova',
        bodyEmphasis:
          'categoria: negócios que operam com inteligência, não somente com software.',
      },
      promise: {
        title: 'A promessa',
        bodyLead:
          'Conduzimos organizações da IA experimental à IA industrializada.',
        bodyEmphasis:
          'De agentes isolados a sistemas orquestrados, do insight à execução.',
      },
      framework: {
        title: 'A estrutura',
        bodyLead:
          'O Human Orchestrated Model™ é estruturado em três camadas que',
        bodyEmphasis:
          'transformam dados, agentes e governança humana em resultados mensuráveis.',
      },
    },
  },
  hom: {
    title: 'Como o Human Orchestrated Model™ funciona:',
    subtitle:
      'Os dados habilitam a inteligência. A governança direciona. A mensuração aperfeiçoa.',
    callouts: {
      performance: {
        title: 'Gestão de Performance',
        body: [
          'Endereçamos o seu desafio de gerar valor e priorizar casos de uso ao incorporar a mensuração de resultados já na concepção de sistemas inteligentes.',
          'Assim, garantimos que toda a capacidade da inteligência esteja diretamente ligada a impacto real no negócio e aprenda continuamente a melhorar seu desempenho.',
        ],
      },
      human: {
        title: 'Humano no Comando',
        body: [
          'Atuamos nos seus desafios de alinhamento do negócio, ao transformar a IA em um sistema organizacional governado e seguro.',
          'Garantimos que a inteligência opere dentro de uma estrutura controlada, transparente e responsável, evitando o caos de agentes de IA.',
        ],
      },
      foundation: {
        title: 'A Base da Inteligência',
        body: [
          'Enfrentamos seus desafios de dados e disciplina financeira, construindo bases de dados sólidas, governadas e operacionais.',
          'Desta forma, permitimos que sistemas inteligentes compreendam o contexto e ajam com eficácia. Levando a sua empresa da IA experimental à IA industrializada.',
        ],
      },
    },
  },
  eras: {
    headerLine1: 'A IA sozinha não transforma os negócios.',
    headerLine2: 'A arquitetura da inteligência, sim.',
    headerLine3: 'E a Keyrus vem fazendo isso há mais de 30 anos:',
    titles: {
      business: 'Inteligência de negócios',
      digital: 'Inteligência digital',
      data: 'Inteligência de dados',
      predictive: 'Inteligência preditiva',
      augmented: 'Inteligência ampliada',
      architected: 'Arquitetura da inteligência',
    },
    bodies: {
      business:
        'Trabalhamos com empresas na construção de suas primeiras bases de dados. Por meio de data warehousing e ETL, conectamos o que antes estava fragmentado e entregamos para as empresas algo que muitas nunca tiveram: uma versão única e estruturada da verdade.',
      digital:
        'Ajudamos as empresas a conectarem os pontos entre sistemas internos e o cenário digital em expansão. Desenvolvemos soluções que viabilizaram dashboards em tempo real. Levamos inteligência em tempo real aos líderes, na velocidade que seus negócios exigiam.',
      data: 'A Keyrus apoiou organizações a extraírem valor em big data. Ajudamos a encontrar padrões em meio ao ruído. A inteligência deixou de ser apenas sobre saber o que aconteceu ou o que estava acontecendo. Passou a ser sobre revelar o que estava oculto.',
      predictive:
        'Ajudamos nossos clientes a evoluir da análise do passado para a antecipação do futuro, com modelos e capacidades que tornam a previsão parte da operação, não apenas uma iniciativa isolada.',
      augmented:
        'A Keyrus ajudou levar IA generativa e agentes inteligentes para as empresas, ampliando a capacidade de decisão humana ao invés de substituí-la. Passamos a ver a IA e a inteligência humana como parceiras, não como concorrentes.',
    },
  },
  contact: {
    title: 'Keyrus, Architect of intelligence',
    // Translated for Brazil per client feedback (July 2026); the title stays
    // in English (brand tagline).
    subtitle: 'Nós operacionalizamos a inteligência',
    submitLabel: 'Fale com nossos arquitetos',
    hubspotLocale: 'pt-br',
    // Client-side relabels for the embedded HubSpot form (served in English),
    // keyed by the field's `name` attribute. Rewritten by KeyrusAiContact.
    fields: {
      firstname: 'Nome',
      lastname: 'Sobrenome',
      email: 'E-mail corporativo',
      phone: 'Telefone',
      company: 'Empresa',
      message: 'Mensagem',
    },
    policy: {
      consentLead:
        'Ao enviar este formulário, você concorda que a Keyrus poderá tratar as informações fornecidas para responder à sua solicitação. Para saber como tratamos seus dados pessoais e seus direitos, leia nossa',
      privacyLabel: 'Política de Privacidade',
      moreLead: 'Para mais informações, consulte nosso',
      termsLabel: 'Aviso Legal e Termos de Uso',
    },
  },
  nav: [
    {label: 'A nova categoria', href: '#hero'},
    {label: 'Conheça a Keyrus', href: '#meet-keyrus'},
    {label: 'HOM™', href: '#hom'},
    {label: 'A era da inteligência', href: '#intelligence-eras'},
    {label: 'Contato', href: '#contact'},
  ],
}

const CONTENT = {es: ES}

export const getKeyrusAiContent = nodeLocale => {
  const key = getKeyrusAiLocaleKey(nodeLocale)

  // pt-BR (Brazil) and pt-PT (Portugal) both resolve to the 'pt' family key but
  // have distinct copy. Match on the full locale, defaulting to European
  // Portuguese for any other pt-* locale.
  if (key === 'pt') {
    return String(nodeLocale).toLowerCase() === 'pt-br' ? PT_BR : PT_PT
  }

  return CONTENT[key] || {}
}
